import {
  debtRegistryKeys,
  communication_template_hooks,
  useCustomer,
  sms_communication_hooks,
  sms_communication_template_hooks,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { sms } from '@axo/shared/data-access/types';
import { Button, NonIdealState } from '@blueprintjs/core';
import { ApplicationContext } from '../../../store/context';
import { useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTypedFlags } from '@axo/shared/services/feature-flags';
import { ACEFlags } from '../../../utils/ACEFlags';
import styles from './RequestDebtRegistryConsent.module.css';
import { convertToE164 } from '@axo/shared/util/formatters';

export function RequestDebtRegistryConsent({
  applicationID,
  customerID,
  SSN,
}: {
  applicationID: string;
  customerID: string;
  SSN: string;
}) {
  const { state } = useContext(DataAccessContext);
  const { state: appState } = useContext(ApplicationContext);
  const customer = useCustomer(customerID);
  const application = useLoanApplication(applicationID);
  const { mutateAsync: createSMS, isLoading: isCreatingSMS } =
    sms_communication_hooks.useCreateCommunicationSMS(
      convertToE164({
        phoneNumber: customer.data?.MobilePhoneNumber ?? '',
        phoneCountryCode:
          customer.data?.MobilePhoneCountryCode?.toString() ?? '',
      }) ?? '',
    );
  const client = useQueryClient();
  const [consentRequestSMSSent, setConsentRequestSMSSent] = useState(false);

  const smsTemplates = sms_communication_template_hooks.useSMSTemplates(
    appState.marketCountry ?? '',
    application.data?.Meta?.Broker ?? 0,
  );

  const {
    mutateAsync: fillCommunicationTemplate,
    isLoading: isFillingCommunicationTemplate,
  } = communication_template_hooks.useFillCommunicationTemplate();
  const { flag_request_constent_sms_template_id } = useTypedFlags(ACEFlags);

  function refetchDebtRegistry() {
    client.invalidateQueries(
      debtRegistryKeys.root({
        applicationID,
        SSN,
      }),
    );
  }

  const requestConsentSMSTemplate = smsTemplates.data?.Items.find(
    (item) => item.ID === flag_request_constent_sms_template_id,
  );

  const requestConsentSMS: sms.CreateSMS = {
    CreatorID: state.user.clientID,
    LoanApplicationID: applicationID,
    TemplateID: flag_request_constent_sms_template_id,
    MarketCountry: appState.marketCountry || '',
    CustomerID: customerID,
    Body: requestConsentSMSTemplate?.Body ?? '',
    Transactional: requestConsentSMSTemplate?.Transactional ?? false,
    Source: 'ACE',
    Meta: {
      Broker: application.data?.Meta?.Broker,
      Brand: application.data?.Meta?.Brand,
    },
  };

  async function requestConsentHandler() {
    const response = await fillCommunicationTemplate({
      LoanApplicationID: applicationID,
      LoanQuoteID: '',
      Body: requestConsentSMS.Body,
    });

    const toCreate = {
      ...requestConsentSMS,
      Body: response.Body,
    };

    await createSMS(toCreate, {
      onSuccess: () => {
        setConsentRequestSMSSent(true);
      },
    });
  }

  return (
    <NonIdealState
      className={styles.padding}
      icon="warning-sign"
      title="Customer has not given consent."
    >
      {consentRequestSMSSent ? (
        <>
          <Button intent="primary" onClick={refetchDebtRegistry} icon="refresh">
            Reload Debt Registry data
          </Button>
          <Button
            intent="none"
            onClick={requestConsentHandler}
            loading={isFillingCommunicationTemplate || isCreatingSMS}
            disabled={customerID !== application.data?.CustomerID}
          >
            Resend SMS: Request Debt Registry Consent
          </Button>
        </>
      ) : (
        <Button
          intent="primary"
          onClick={requestConsentHandler}
          loading={isFillingCommunicationTemplate || isCreatingSMS}
          disabled={customerID !== application.data?.CustomerID}
        >
          Send SMS: Request Debt Registry Consent
        </Button>
      )}
    </NonIdealState>
  );
}
